<script setup>
import {defineProps, ref, computed, onMounted, onUnmounted, inject} from "vue";
import taste from "@/assets/animations/taste.json";
import warm from "@/assets/animations/warm.json";
import InputAutocomplete from "@/components/InputAutocomplete";
import { useClipboard } from '@vueuse/core';
import { useWindowSize } from '@vueuse/core';
const { width } = useWindowSize();

const props = defineProps({
  data: {
    type: Object,
    default: () => {}
  }
});

const postfix = props.data.quiz === 's2026' ? '-s' : '';

const sharingActive = ref(false);
const selectedName = ref(null);
const topBlock = ref(null);
const topBlockGap = ref(0);
const actionOnSide = ref(false);
const pageURL = window.location.href;

const linkToShare = computed(() => {
  return `${window.location.origin}/videoquiz/${props.data.quiz}/${props.data.selection}/${ selectedName.value?.file || 'other'}`;
})

const { copy, copied, isSupported } = useClipboard({ linkToShare })

const sharingUrl = (platform) => {
  const encodedUrl = encodeURIComponent(linkToShare.value);
  const sharingText =
      (selectedName.value && selectedName.value?.file !== 'other') ?
          `${selectedName.value?.name}, коктейль «${props.data.recipe.title}» для тебя` :
          `Коктейль «${props.data.recipe.title}» для тебя`;
  const platforms = {
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${sharingText}`,
    whatsapp: `https://api.whatsapp.com/send?text=${sharingText} ${encodedUrl}`
  };

  return platforms[platform] || '';
};

const openPopup = () => {
  document.body.classList.add('_lock');
  sharingActive.value = true;
}
const closePopup = (event) => {
  if (event?.target.classList[0] === 'sharing' || event?.target.classList[0] === 'sharing__close') {
    document.body.classList.remove('_lock');
    sharingActive.value = false;
  }
}

const handleScroll = () => {
  const scrolled = window.pageYOffset || document.documentElement.scrollTop;
  actionOnSide.value = scrolled > topBlockGap.value;
}

const DLEvent = inject('DLEvent')

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
  topBlockGap.value = topBlock.value.getBoundingClientRect().height + 260;
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

</script>

<template>
  <div class="recipe">
    <div class="recipe__top" :style="{'background-image': `url(${ data.recipe.image || data.recipe.images[0]})`}" ref="topBlock">
      <img :src="require(`@/assets/img/decor5${postfix}.svg`)" alt="" class="_decor" v-if="width >= 1024"/>
      <img :src="require(`@/assets/img/decor3${postfix}.svg`)" alt="" class="_decor" v-else/>
      <h2 v-if="data.userName && data.name !== 'other'">{{ data.userName }}, этот коктейль<br/> «{{ data.recipe.title }}»<br/> для&nbsp;тебя</h2>
      <h2 v-else>Этот коктейль<br/> «{{ data.recipe.title }}»<br/> для&nbsp;тебя</h2>
      <div class="recipe__top-image">
        <img :src="data.recipe.image || data.recipe.images[0]">
      </div>
    </div>
    <div class="recipe__inner">
      <div class="recipe__actions">
        <div class="recipe__actions-wrap">
          <a
            href="#"
            @click.prevent="openPopup(); DLEvent('festive_share_recipe', 'pageURL', pageURL)"
            class="animated-button animated-button_no-shadow animated-button_brown"
          >
            <Vue3Lottie :animationData="taste" :loop="false" :rendererSettings="{preserveAspectRatio: 'none'}" />
            <span>поделиться рецептом</span>
          </a>
          <a
            href="https://t.me/addstickers/FESTIVE_NESCAFE"
            target="_blank"
            class="animated-button animated-button_no-shadow animated-button_red"
            @click="DLEvent('festive_get_stickerpack', 'pageURL', pageURL)"
          >
            <Vue3Lottie :animationData="warm" :loop="false" :rendererSettings="{preserveAspectRatio: 'none'}"/>
            <span>забрать стикерпак</span>
          </a>
        </div>
        <!--a href="/festive-activation" class="recipe__get-new" @click="DLEvent('festive_get_new_cocktail', 'pageURL', pageURL)">
          <inline-svg :src="require('@/assets/img/new.svg')"></inline-svg>
          <span>Получить новый коктейль</span>
        </a-->
      </div>
      <Transition>
        <div class="recipe__actions recipe__actions_side" v-if="actionOnSide && width >= 1280">
          <!--a href="/festive-activation" class="recipe__get-new" @click="DLEvent('festive_get_new_cocktail', 'pageURL', pageURL)">
            <inline-svg :src="require('@/assets/img/new.svg')"></inline-svg>
            <span>Получить новый коктейль</span>
          </a-->
          <a
            href="#"
            @click.prevent="openPopup(); DLEvent('festive_share_recipe', 'pageURL', pageURL)"
            class="animated-button animated-button_no-shadow animated-button_brown"
          >
            <Vue3Lottie :animationData="taste" :loop="false" :rendererSettings="{preserveAspectRatio: 'none'}" />
            <span>поделиться рецептом</span>
          </a>
          <a
            href="https://t.me/addstickers/FESTIVE_NESCAFE"
            target="_blank"
            class="animated-button animated-button_no-shadow animated-button_red"
            @click="DLEvent('festive_get_stickerpack', 'pageURL', pageURL)"
          >
            <Vue3Lottie :animationData="warm" :loop="false" :rendererSettings="{preserveAspectRatio: 'none'}"/>
            <span>забрать стикерпак</span>
          </a>
        </div>
      </Transition>
      <div class="recipe__main">
        <p class="recipe__intro">
          Приготовь коктейль дома,<br/> порадуй себя и&nbsp;близких
          <img :src="require('@/assets/img/decor1.svg')" class="_decor1" alt=""/>
          <img :src="require(`@/assets/img/decor2${postfix}.svg`)" class="_decor2" alt=""/>
        </p>
        <h2>Пошаговый рецепт</h2>
        <div class="recipe__ingridients">
          <h3>Вам понадобится</h3>
          <ul>
            <li v-for="item in data.recipe.ingridients" :key="item">
              <img :src="require('@/assets/img/check.svg')" alt=""/>
              <span v-html="item"></span>
            </li>
          </ul>
        </div>
        <div class="recipe__advices" v-if="data.recipe.advices.length">
          <h3>Полезные советы</h3>
          <div>
            <ul>
              <li v-for="advice in data.recipe.advices" :key="advice" v-html="advice"></li>
            </ul>
          </div>
        </div>
        <div class="recipe__products _visible-desktop-small">
          <a
            v-for="product in data.recipe.products"
            :key="product.title"
            :href="product.url"
            class="recipe__product"
            @click="DLEvent('festive_product_click', 'productURL', product.url, 'pageURL', pageURL)"
          >
            <div class="recipe__product-image">
              <img :src="product.img" :alt="product.title"/>
            </div>
            <div>
              <p class="recipe__product-taste">{{ product.taste }}</p>
              <p :class="['recipe__product-title', {'_gold': product.url.includes('nescafe-gold-buy')}]">{{ product.title }}</p>
            </div>
          </a>
        </div>
        <div class="recipe__steps">
          <h2>Попробуйте сами!</h2>
          <video
            v-if="data.recipe.video.url"
            playsinline
            preload="auto"
            controls
            class="recipe__video"
            :poster="data.recipe.video.preview"
          >
            <source :src="data.recipe.video.url" type="video/mp4">
          </video>
          <div v-for="step in data.recipe.steps" :key="step.num" class="recipe__step">
            <h4 class="recipe__step-num">{{ step.num }}</h4>
            <div v-html="step.desc" class="recipe__step-descr"></div>
            <img :src="step.img" alt="" class="recipe__step-image">
          </div>
        </div>
        <div class="recipe__advices" v-if="data.recipe.descriptions.length">
          <h3>На заметку</h3>
          <div v-html="data.recipe.descriptions"></div>
        </div>
        <!--a
          href="/festive-activation"
          class="recipe__to-quiz"
          @click="DLEvent('festive_get_new_cocktail', 'pageURL', pageURL)"
        >
          <inline-svg :src="require('@/assets/img/button.svg')" v-if="width >= 730"></inline-svg>
          <inline-svg :src="require('@/assets/img/button-mobile.svg')" v-else></inline-svg>
          получить новый коктейль
        </a-->
      </div>
      <div class="recipe__products _hidden-desktop-small">
        <a
          v-for="product in data.recipe.products"
          :key="product.title"
          :href="product.url"
          class="recipe__product"
          @click="DLEvent('festive_product_click', 'productURL', product.url, 'pageURL', pageURL)"
        >
          <div class="recipe__product-image">
            <img :src="product.img" :alt="product.title"/>
          </div>
          <div>
            <p class="recipe__product-taste">{{ product.taste }}</p>
            <p :class="['recipe__product-title', {'_gold': product.url.includes('nescafe-gold-buy')}]">{{ product.title }}</p>
          </div>
        </a>
      </div>
    </div>
  </div>
  <div class="sharing" v-if="sharingActive" @click="closePopup">
    <div class="sharing__box">
      <span class="sharing__close" @click="closePopup(); selectedName = null;">
        <inline-svg :src="require('@/assets/img/close.svg')"></inline-svg>
      </span>
      <div class="sharing__card">
        <img :src="data.recipe.image || data.recipe.images[0]" alt="" class="_image"/>
        <img :src="require(`@/assets/img/decor4${postfix}.svg`)" alt="" class="_decor">
        <p>Этот коктейль<br/> «{{ data.recipe.title }}» для&nbsp;тебя</p>
      </div>
      <div class="sharing__actions">
        <InputAutocomplete v-model="selectedName" :names="data?.names" placeholder="Введи имя получателя" alt />
        <div class="sharing__buttons">
          <a
            :href="sharingUrl('telegram')"
            target="_blank"
            class="_telegram"
            @click="DLEvent('festive_share_click', 'share_type', 'tg', 'pageURL', linkToShare)"
          >
            <inline-svg :src="require('@/assets/img/telegram-frame.svg')" class="_frame"></inline-svg>
            <inline-svg :src="require('@/assets/img/telegram-icon.svg')"></inline-svg>
          </a>
          <a
            :href="sharingUrl('whatsapp')"
            target="_blank"
            class="_whatsapp"
            @click="DLEvent('festive_share_click', 'share_type', 'wa', 'pageURL', linkToShare)"
          >
            <inline-svg :src="require('@/assets/img/whatsapp-frame.svg')" class="_frame"></inline-svg>
            <inline-svg :src="require('@/assets/img/whatsapp-icon.svg')"></inline-svg>
          </a>
        </div>
      </div>
      <div v-if="isSupported" class="sharing__copy-link">
        <button @click="copy(linkToShare); DLEvent('festive_share_click', 'share_type', 'link', 'pageURL', linkToShare)">
          <span v-show="copied">
            <img :src="require('@/assets/img/check.svg')" alt=""/>
            ссылка скопирована
          </span>
          <span v-show="!copied">
            <img :src="require('@/assets/img/copy.svg')" alt=""/>
            скопировать ссылку на&nbsp;рецепт
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>